import {
  AfterViewInit, ChangeDetectionStrategy, Component, EventEmitter, HostBinding,
  Inject, Input, OnInit, Output, PLATFORM_ID, Signal, ViewEncapsulation
} from '@angular/core';

import { DOCUMENT, isPlatformBrowser, NgClass } from '@angular/common';
import { SwiperOptions } from 'swiper/types';
import { Subscription } from 'rxjs';

import { DeviceService, OrientationEnum, PositionEnum, SizesEnum } from '@seb/web-design-system/core';
import { SdSliderComponent } from '@seb/web-design-system/sliders';

import type { ITilesGroup } from './tiles-group.interface';
import type { ITileModal } from './tile-modal/tile-modal.interface';
import { SdTileModalComponent } from './tile-modal/tile-modal.component';
import { SdTileComponent } from './tile/tile.component';
import { toSignal } from '@angular/core/rxjs-interop';

/** @dynamic */
@Component({
  selector: 'sd-tiles-group',
  templateUrl: './tiles-group.component.html',
  styleUrls: ['./tiles-group.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    NgClass,
    SdTileComponent,
    SdSliderComponent,
    SdTileModalComponent
  ]
})
export class SdTilesGroupComponent implements AfterViewInit, OnInit {
  @Input()
  context = 'Tiles group';

  @Input()
  tilesGroup: ITilesGroup;

  /**
   * Allow to define the level of the title tag (h1 - h6) for each tile.
   * * @param {number} 1 to 6
   */
  @Input()
  tileTitleLevel = 4;

  @Input()
  position: PositionEnum.left | PositionEnum.right = PositionEnum.left;

  @Input()
  tilesSliderConfig: SwiperOptions = {
    breakpoints: {
      0: {
        slidesPerView: 1
      },
      768: {
        slidesPerView: 1
      },
      1280: {
        slidesPerView: 1
      }
    }
  };

  /**
   * Allow to define the global layout of the component
   * * @param {string} portrait | landscape
   */
  @Input()
  get layout() {
    return this._layout;
  }
  set layout(layout: OrientationEnum) {
    if (!layout) {
      throw new Error(
        `The config you're giving is ${layout}. \n
        See types: ${OrientationEnum.landscape}, ${OrientationEnum.portrait}
      `,
      );
    }
    this._layout = layout;
  }

  @Input()
  twicPicsAutoSize = true;

  @Output()
  videoPlayed = new EventEmitter<{ status: string; title: string }>();

  @Output()
  videoEnded = new EventEmitter<{ status: string; title: string }>();

  @Input() class = ''; // override the standard class attr with a new one.
  @HostBinding('class')
  get hostClasses(): string {
    return [this.class, 'is-' + this._layout, 'is-' + this.position]
      .filter(Boolean)
      .join(' ');
  }

  _layout: OrientationEnum = OrientationEnum.portrait;
  sizesEnum = SizesEnum;
  isDevice: Signal<boolean>;
  deviceSubscription: Subscription;
  modalContent: ITileModal;
  isOpened: boolean;
  positionEnum = PositionEnum;
  bodyChildren;
  currentTargetBtnModal: HTMLElement;
  tileSize: number;

  constructor(
    @Inject(PLATFORM_ID) private platformId: any,
    @Inject(DOCUMENT) private document: Document,
    private deviceService: DeviceService
  ) {
    this.isDevice = toSignal(this.deviceService.isDevice);
  }

  ngOnInit() {
    this.tileSize = this.tilesGroup.tiles.length;
    /*
     * A11y
     */
    this.tilesGroup.tiles.forEach((tile, index) => {
      tile.idTitleTile = 'tile-' + (index + 1);
    });
  }

  ngAfterViewInit() {
    if (isPlatformBrowser(this.platformId)) {
      this.bodyChildren = this.document.body.children;
    }
  }

  onOpenModal(tile, event) {
    this.isOpened = true;
    this.modalContent = tile.modal;
    /*
     * A11y
     */
    this.currentTargetBtnModal = event.target;
    for (const child of this.bodyChildren) {
      if (child.className !== 'cdk-overlay-container') {
        child.setAttribute('aria-hidden', true);
      }
    }
  }

  onOpenChange(isOpen: boolean) {
    /*
     * A11y
     */
    if (!isOpen) {
      this.currentTargetBtnModal.focus();
      for (const child of this.bodyChildren) {
        child.removeAttribute('aria-hidden');
      }
    }
  }

  onVideoPlayed(event) {
    this.videoPlayed.emit(event);
  }

  onVideoEnded(event) {
    this.videoEnded.emit(event);
  }
}
